import type { CrmNoteType } from '@plato/data/prisma';
import { format, formatISO, isToday, min, roundToNearestMinutes } from 'date-fns';
import { CalendarIcon, CalendarPlus, ClockIcon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { CustomerIcon } from '../../../assets/icons/customer-icon.tsx';
import { Button } from '../../../components/ui/button.tsx';
import { DatePicker } from '../../../components/ui/date-picker.tsx';
import { LinkText } from '../../../components/ui/link-text.tsx';
import TimePicker from '../../../components/ui/time-picker.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useVoiceRecording } from '../../../lib/contexts/voice-recording-context.tsx';
import { useDiscardChangesWarning } from '../../../lib/hooks/use-discard-changes-warning.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CustomerDTO } from '../../../server/dto/customer-dto.ts';
import type { RouterInput } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { AnalyticsEventPayload } from '../../analytics/types/analytics-events.ts';
import { FileUploader } from '../../crm-attachment/components/file-uploader.tsx';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import { Dialog } from '../../dialog/dialog.tsx';
import type { ModalInnerProps } from '../../modal/index.tsx';
import type { AIActionType } from '../../rich-text/hooks/use-improve-writing.ts';
import { CustomerSelect } from '../../shared/components/form-items-with-fetch/customer-select.tsx';
import { NoteFormFields } from './note-form-fields.tsx';
import { NoteTypeSelector } from './note-type-selector.tsx';

export type CreateNoteFormProps = {
    customer?: CustomerDTO;
    onCancel?: () => void;
    onSuccess?: () => void;
    onIsDirtyChange?: (isDirty: boolean) => void;
    analyticsSource?: AnalyticsEventPayload<'customer_interaction_created'>['page_source'];
};

const getMaxDate = (): Date => roundToNearestMinutes(new Date(), { roundingMethod: 'floor', nearestTo: 15 });

export const CreateNoteForm = ({
    customer,
    onSuccess,
    onCancel,
    onIsDirtyChange,
    analyticsSource,
}: CreateNoteFormProps): ReactNode => {
    const { t } = useLocale();
    const { toast } = useToast();
    const utils = api.useUtils();
    const { currentSessionId } = useVoiceRecording();
    const { enableFileUpload, enableCreateNextEventFakeDoor } = useAppConfig();

    const sendEvent = useAnalytics();
    const [isAIBusy, setIsAIBusy] = useState<boolean>(false);
    const [aiSessionId, setAISessionId] = useState<string | undefined>(undefined);

    const handleAIInteraction = (action: AIActionType, sessionId: string): void => {
        switch (action) {
            case 'accept':
                // Store the session ID for any AI interaction (we'll use it when the note is created)
                setAISessionId(sessionId);
                setIsAIBusy(false);
                break;
            case 'request':
            case 'regenerate':
                setIsAIBusy(true);
                break;
            case 'reject':
                setIsAIBusy(false);
                break;
            default:
                // no-op for unknown actions
                break;
        }
    };

    const [noteType, setNoteType] = useState<CrmNoteType>('VISIT');

    const defaultValues = {
        title: undefined,
        customerId: customer?.customerId,
        occurredAt: getMaxDate().toISOString(),
        attachments: undefined,
    };

    const {
        control,
        handleSubmit,
        clearErrors,
        watch,
        formState: { errors: formErrors, isDirty },
    } = useForm<Omit<RouterInput['platoUser']['command']['notes']['create'], 'noteType'>>({
        defaultValues,
    });

    const watchedCustomerId = watch('customerId');

    useEffect(() => {
        clearErrors('title');
    }, [noteType, clearErrors]);

    useEffect(() => {
        onIsDirtyChange?.(isDirty);
    }, [isDirty, onIsDirtyChange]);

    const getSuccessToastTitle = (noteType: CrmNoteType): string => {
        switch (noteType) {
            case 'VISIT':
                return t('notes__form_create_toast__success_title__event', { ns: 'notes' });
            case 'PHONE_CALL':
                return t('notes__form_create_toast__success_title__phone_call', { ns: 'notes' });
            default:
                return t('notes__form_create_toast__success_title', { ns: 'notes' });
        }
    };

    const createMutation = api.platoUser.command.notes.create.useMutation({
        onSuccess: async (note) => {
            // Invalidate activity feed queries to refresh the data
            await utils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate();

            const toastTitle = getSuccessToastTitle(noteType);

            toast({
                icon: 'success',
                variant: 'default',
                title: toastTitle,
                createdLink: <LinkText href={`/customers/${note.customerId}/activity`} />,
            });

            sendEvent('customer_interaction_created', {
                note_id: note.noteId,
                customer_id: note.customerId,
                note_type: noteType,
                ai_session_id: aiSessionId,
                page_source: analyticsSource,
            });

            onSuccess?.();
        },
        onError: (error) => {
            toast({
                icon: 'warning',
                title: 'Error',
                description: error.message,
            });
        },
    });

    const onSubmit = handleSubmit((data) => {
        createMutation.mutate({
            ...data,
            occurredAt: noteType === 'NOTE' ? undefined : data.occurredAt,
            noteType,
        });
    });

    const handleCreateNextEventClick = (): void => {
        sendEvent('create_next_event_from_log', {
            customer_id: watchedCustomerId,
        });
        toast({
            icon: 'info',
            title: t('fake_door_next_event_toast_title', { ns: 'events' }),
            description: t('fake_door_next_event_toast_description', { ns: 'events' }),
        });
    };

    return (
        <DialogForm className="sm:max-w-2xl">
            <DialogForm.Header title={t('notes__form_create_title', { ns: 'notes' })} />
            <DialogForm.Content>
                <NoteTypeSelector value={noteType} onChange={setNoteType} />
                <NoteFormFields
                    noteType={noteType}
                    control={control}
                    errors={formErrors}
                    onAIInteraction={handleAIInteraction}
                />
                <DialogForm.ItemList grid>
                    {/* Customer Field */}
                    <DialogForm.Item className="items-start">
                        <DialogForm.Label htmlFor="customerId" icon={CustomerIcon} required>
                            {t('notes__form__customer_label', { ns: 'notes' })}
                        </DialogForm.Label>
                        <DialogForm.Body className="flex flex-col gap-1">
                            <Controller
                                name="customerId"
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: t('form__customer_required', { ns: 'common' }),
                                    },
                                }}
                                render={({ field: { value, onChange } }) => (
                                    <CustomerSelect
                                        customer={customer}
                                        value={value}
                                        onChange={onChange}
                                        className="w-full"
                                        variant="inline"
                                        error={!!formErrors.customerId}
                                        tracking={{
                                            context: 'note_customer_select',
                                            context_entity_type: 'note',
                                        }}
                                    />
                                )}
                            />
                            <DialogForm.Error error={formErrors.customerId} />
                        </DialogForm.Body>
                    </DialogForm.Item>

                    {/* Date and Time for VISIT and PHONE_CALL */}
                    {noteType !== 'NOTE' && (
                        <>
                            {/* Date Field */}
                            <DialogForm.Item className="items-start">
                                <DialogForm.Label htmlFor="businessDate" icon={CalendarIcon} required>
                                    {t('notes__form__date_label', { ns: 'notes' })}
                                </DialogForm.Label>
                                <DialogForm.Body className="flex flex-col gap-1">
                                    <Controller
                                        name="occurredAt"
                                        control={control}
                                        render={({ field }) => (
                                            <DatePicker
                                                disableIcon
                                                mode="single"
                                                value={field.value ? new Date(field.value) : new Date()}
                                                valuePrefix=""
                                                onChange={(value) => {
                                                    if (!value) {
                                                        return;
                                                    }

                                                    const baseDate = field.value
                                                        ? new Date(field.value)
                                                        : new Date();

                                                    const timePart = formatISO(baseDate, {
                                                        representation: 'time',
                                                    });

                                                    const datePart = formatISO(value, {
                                                        representation: 'date',
                                                    });

                                                    const nextDate = new Date(datePart + 'T' + timePart);

                                                    field.onChange(
                                                        min([getMaxDate(), nextDate]).toISOString(),
                                                    );
                                                }}
                                                disabled={{ after: new Date() }}
                                                className="w-full"
                                            />
                                        )}
                                    />
                                </DialogForm.Body>
                            </DialogForm.Item>

                            {/* Time Field */}
                            <DialogForm.Item className="items-start">
                                <DialogForm.Label htmlFor="businessTime" icon={ClockIcon} required>
                                    {t('notes__form__time_label', { ns: 'notes' })}
                                </DialogForm.Label>
                                <DialogForm.Body className="flex flex-col gap-1">
                                    <Controller
                                        name="occurredAt"
                                        control={control}
                                        render={({ field }) => (
                                            <TimePicker
                                                value={
                                                    field.value
                                                        ? format(new Date(field.value), 'HH:mm:ss')
                                                        : null
                                                }
                                                onChange={(value) => {
                                                    const baseDate = field.value
                                                        ? new Date(field.value)
                                                        : new Date();

                                                    const datePart = formatISO(baseDate, {
                                                        representation: 'date',
                                                    });

                                                    const nextDate = new Date(datePart + 'T' + value);

                                                    field.onChange(nextDate.toISOString());
                                                }}
                                                max={
                                                    !field.value || isToday(field.value)
                                                        ? format(getMaxDate(), 'HH:mm:ss')
                                                        : undefined
                                                }
                                                placeholder={t('time_picker__placeholder', { ns: 'common' })}
                                            />
                                        )}
                                    />
                                </DialogForm.Body>
                            </DialogForm.Item>
                        </>
                    )}
                    {enableFileUpload && (
                        <Controller
                            name="attachments"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <FileUploader
                                    value={value ?? []}
                                    onChange={onChange}
                                    analyticsContext="note"
                                />
                            )}
                        />
                    )}
                </DialogForm.ItemList>
                {enableCreateNextEventFakeDoor && (
                    <div className="self-start">
                        <Button variant="secondary" icon={CalendarPlus} onClick={handleCreateNextEventClick}>
                            {t('fake_door_next_event_button', { ns: 'events' })}
                        </Button>
                    </div>
                )}
            </DialogForm.Content>
            <DialogForm.SubmitBlock
                onCancel={onCancel}
                onSubmit={() => {
                    void onSubmit();
                }}
                disabled={createMutation.isPending || isAIBusy || !!currentSessionId}
            />
        </DialogForm>
    );
};

type NoteFormDialogProps = ModalInnerProps & CreateNoteFormProps;

export const NoteFormDialog = DialogStateManager.create<NoteFormDialogProps>(({ onSuccess, ...props }) => {
    const { visible, remove } = useDialog();
    const [isDirty, setIsDirty] = useState(false);
    const { confirmDiscard } = useDiscardChangesWarning(isDirty);

    const handleClose = (): void => {
        confirmDiscard(remove);
    };

    return (
        <Dialog open={visible} showClose onClose={handleClose}>
            <CreateNoteForm
                onCancel={handleClose}
                onSuccess={() => {
                    remove();
                    onSuccess?.();
                }}
                onIsDirtyChange={setIsDirty}
                {...props}
            />
        </Dialog>
    );
});
